import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAccount } from "wagmi";

/* ─── Constants ─────────────────────────────────────────────────────────────── */
const SOURCE_CHAINS = ["BSC", "ETH", "SOL", "XRP", "RH", "BESC"];
const TAX_PURPOSES = ["team", "dev", "marketing", "rewards", "liquidity", "buyback", "other"];
const TAX_PURPOSE_LABELS = { team: "Team", dev: "Dev", marketing: "Marketing", rewards: "Rewards", liquidity: "Auto-LP", buyback: "Buyback", other: "Other" };
const TAX_MODES = [
  { key: "all",          label: "All Transfers" },
  { key: "buy",          label: "Buy Only" },
  { key: "sell",         label: "Sell Only" },
  { key: "differential", label: "Buy & Sell (Different Rates)" },
];
const ALL_CHAINS = ["ETH", "BSC", "BESC", "RH", "XRP", "SOL"];
const STATUSES = ["pending", "completed", "failed"];
const PAGE_SIZE = 50;
const SESSION_KEY = "besc_admin_token";
const SESSION_EXPIRY_KEY = "besc_admin_token_exp";

/* ─── Util ───────────────────────────────────────────────────────────────────── */
const fmt = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};
const shortAddr = (h) => { if (!h) return "—"; if (h.length <= 14) return h; return `${h.slice(0, 6)}…${h.slice(-4)}`; };
const shortHash = (h) => { if (!h) return "—"; if (h.length <= 14) return h; return `${h.slice(0, 8)}…${h.slice(-6)}`; };
const statusColor = (s) => {
  if (s === "completed" || s === "complete" || s === "live" || s === true) return "#4ecb71";
  if (s === "failed" || s === false) return "#e05c5c";
  return "#d4a843";
};

/* ─── Session hook ───────────────────────────────────────────────────────────── */
function useSession() {
  const [token, setTokenState] = useState(() => {
    try {
      const t = sessionStorage.getItem(SESSION_KEY);
      const exp = parseInt(sessionStorage.getItem(SESSION_EXPIRY_KEY) || "0");
      if (t && exp > Date.now()) return t;
    } catch { /* private mode */ }
    return null;
  });

  const setToken = useCallback((t, expiresAt) => {
    setTokenState(t);
    try {
      if (t) {
        sessionStorage.setItem(SESSION_KEY, t);
        sessionStorage.setItem(SESSION_EXPIRY_KEY, String(new Date(expiresAt).getTime()));
      } else {
        sessionStorage.removeItem(SESSION_KEY);
        sessionStorage.removeItem(SESSION_EXPIRY_KEY);
      }
    } catch { /* private mode */ }
  }, []);

  const logout = useCallback(() => setToken(null, 0), [setToken]);
  return { token, setToken, logout };
}

/* ─── Components ─────────────────────────────────────────────────────────────── */
function Alert({ type = "error", children, onClose }) {
  const cls = { error: "ap-alert-error", success: "ap-alert-success", warn: "ap-alert-warn", info: "ap-alert-info" };
  return (
    <div className={`ap-alert ${cls[type] || "ap-alert-error"}`}>
      <span style={{ flex: 1 }}>{children}</span>
      {onClose && <button className="ap-close-btn" onClick={onClose}>✕</button>}
    </div>
  );
}

function Spinner() { return <div className="ap-spinner-wrap"><div className="ap-spinner" /></div>; }

function StatusPill({ status }) {
  return <span className="ap-pill" style={{ color: statusColor(status), borderColor: statusColor(status) }}>{status}</span>;
}

/* ─── Main ───────────────────────────────────────────────────────────────────── */
export default function AdminPanel() {
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const apiKey = process.env.REACT_APP_API_KEY || "";

  const { token, setToken, logout } = useSession();

  /* auth state */
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  /* global */
  const [tab, setTab] = useState("dashboard");
  const [pageErr, setPageErr] = useState("");
  const [loading, setLoading] = useState(false);

  /* dashboard */
  const [stats, setStats] = useState(null);

  /* transactions */
  const [txns, setTxns] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txOffset, setTxOffset] = useState(0);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSrc, setFilterSrc] = useState("all");
  const [filterDest, setFilterDest] = useState("all");
  const [search, setSearch] = useState("");
  const [updateTarget, setUpdateTarget] = useState(null);
  const [updateStatus, setUpdateStatus] = useState("completed");
  const [updateNote, setUpdateNote] = useState("");
  const [updateMsg, setUpdateMsg] = useState("");

  /* assets */
  const [assets, setAssets] = useState([]);

  /* add token */
  const [customTokens, setCustomTokens] = useState([]);
  const [addMode, setAddMode] = useState("deploy");
  const [addForm, setAddForm] = useState({
    tokenName: "", symbol: "", decimals: "", sourceChain: "BSC",
    sourceAddress: "", bridgeFeeBps: "10", notes: "", managerAddress: "", existingWrapped: "",
    // tax
    taxMode: "all",          // 'all' | 'buy' | 'sell' | 'differential'
    taxPct: "",              // unified rate for 'all' mode
    buyTaxPct: "",           // buy rate for 'buy' / 'differential'
    sellTaxPct: "",          // sell rate for 'sell' / 'differential'
    rewardsTokenAddress: "", // ERC20 rewards token on BESC (paid to holders via swapback)
    lpPairedToken: "",       // LP partner token on BESC (for auto-LP)
    lpTaxPct: "",            // % of collected tax routed to auto-LP (0 = disabled)
    // swapback / auto-convert
    swapbackEnabled: false,
    swapbackPayoutType: "native", // "native" = pay in BESC, "erc20" = pay in rewards token
    swapbackRouter: "", swapbackTokenOut: "", swapbackThreshold: "",
  });
  const [taxRows, setTaxRows] = useState([{ wallet: "", pct: "", purpose: "team" }]);
  const [addStatus, setAddStatus] = useState("");
  const [addMsg, setAddMsg] = useState("");
  const [addResult, setAddResult] = useState(null);

  /* manage token (on-chain config editor) */
  const [manageToken, setManageToken] = useState(null);
  const [onchainConfig, setOnchainConfig] = useState(null);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageMsg, setManageMsg] = useState("");
  const [manageMsgType, setManageMsgType] = useState("info");
  const [editTaxBps, setEditTaxBps] = useState("0");
  const [editTaxRows, setEditTaxRows] = useState([{ wallet: "", pct: "" }]);
  const [editTaxBuyBps, setEditTaxBuyBps] = useState("0");
  const [editTaxSellBps, setEditTaxSellBps] = useState("0");
  const [editSwapback, setEditSwapback] = useState({ enabled: false, toNative: true, router: "", tokenOut: "", threshold: "" });
  const [editManager, setEditManager] = useState("");
  const [exemptWallet, setExemptWallet] = useState("");
  const [ammPairInput, setAmmPairInput] = useState("");
  const [editLPConfig, setEditLPConfig] = useState({ lpBps: "", pairedToken: "" });

  /* support tickets */
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportFilter, setSupportFilter] = useState("open");
  const [supportSelected, setSupportSelected] = useState(null);
  const [supportNotesDraft, setSupportNotesDraft] = useState("");
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportMsg, setSupportMsg] = useState("");
  const [supportMsgType, setSupportMsgType] = useState("info");

  /* security */
  const [secEvents, setSecEvents] = useState([]);
  const [blockedWallets, setBlockedWallets] = useState([]);
  const [blockInput, setBlockInput] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [secMsg, setSecMsg] = useState("");
  const [secMsgType, setSecMsgType] = useState("error");
  const [secView, setSecView] = useState("events");

  const refreshTimer = useRef(null);

  /* ─── Auth headers ─────────────────────────────────────────────────────── */
  const authHeaders = useCallback(() => ({
    "x-api-key": apiKey,
    "x-admin-token": token,
    "Content-Type": "application/json",
  }), [apiKey, token]);

  /* ─── Session auto-refresh ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!token) return;
    const tryRefresh = async () => {
      try {
        const r = await fetch(`${backendUrl}/admin/refresh`, { method: "POST", headers: { "x-api-key": apiKey, "x-admin-token": token } });
        if (r.ok) { const j = await r.json(); setToken(j.token, j.expiresAt); }
        else logout();
      } catch { /* network blip */ }
    };
    try {
      const exp = parseInt(sessionStorage.getItem(SESSION_EXPIRY_KEY) || "0");
      const delay = Math.max(60000, exp - Date.now() - 5 * 60 * 1000);
      refreshTimer.current = setTimeout(tryRefresh, delay);
    } catch { /* private mode */ }
    return () => clearTimeout(refreshTimer.current);
  }, [token, backendUrl, apiKey, setToken, logout]);

  /* ─── Login ────────────────────────────────────────────────────────────── */
  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    setLoginErr(""); setLoginLoading(true);
    try {
      const r = await fetch(`${backendUrl}/admin/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: loginEmail.trim(), password: loginPass }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setToken(j.token, j.expiresAt);
      setLoginPass("");
    } catch (err) { setLoginErr(err.message || "Login failed"); }
    finally { setLoginLoading(false); }
  }, [backendUrl, loginEmail, loginPass, setToken]);

  /* ─── API helper ───────────────────────────────────────────────────────── */
  const apiFetch = useCallback(async (path, opts = {}) => {
    const r = await fetch(`${backendUrl}${path}`, { headers: authHeaders(), ...opts });
    const j = await r.json().catch(() => ({}));
    if (r.status === 401) { logout(); throw new Error("Session expired — please log in again"); }
    if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
    return j;
  }, [backendUrl, authHeaders, logout]);

  /* ─── Fetchers ─────────────────────────────────────────────────────────── */
  const fetchStats = useCallback(async () => {
    setLoading(true); setPageErr("");
    try { setStats(await apiFetch("/admin/stats")); }
    catch (e) { setPageErr(e.message); } finally { setLoading(false); }
  }, [apiFetch]);

  const fetchTxns = useCallback(async (offset = 0) => {
    setLoading(true); setPageErr("");
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset, ...(filterStatus !== "all" && { status: filterStatus }), ...(filterSrc !== "all" && { source_chain: filterSrc }), ...(filterDest !== "all" && { dest_chain: filterDest }), ...(search && { search }) });
      const data = await apiFetch(`/admin/transactions?${params}`);
      setTxns(data.transactions); setTxTotal(data.total); setTxOffset(offset);
    } catch (e) { setPageErr(e.message); } finally { setLoading(false); }
  }, [apiFetch, filterStatus, filterSrc, filterDest, search]);

  const fetchAssets = useCallback(async () => {
    setLoading(true); setPageErr("");
    try { setAssets((await apiFetch("/admin/assets")).assets); }
    catch (e) { setPageErr(e.message); } finally { setLoading(false); }
  }, [apiFetch]);

  const fetchCustomTokens = useCallback(async () => {
    setLoading(true); setPageErr("");
    try { setCustomTokens((await apiFetch("/admin/tokens")).tokens); }
    catch (e) { setPageErr(e.message); } finally { setLoading(false); }
  }, [apiFetch]);

  const fetchSecEvents = useCallback(async () => {
    setLoading(true); setPageErr("");
    try { setSecEvents((await apiFetch("/admin/security/events")).events); }
    catch (e) { setPageErr(e.message); } finally { setLoading(false); }
  }, [apiFetch]);

  const fetchBlockedWallets = useCallback(async () => {
    try { setBlockedWallets((await apiFetch("/admin/security/blocked")).blocked); }
    catch (e) { setPageErr(e.message); }
  }, [apiFetch]);

  useEffect(() => {
    if (!token) return;
    if (tab === "dashboard") fetchStats();
    else if (tab === "transactions") fetchTxns(0);
    else if (tab === "assets") fetchAssets();
    else if (tab === "addtoken") fetchCustomTokens();
    else if (tab === "support") fetchSupportTickets();
    else if (tab === "security") { fetchSecEvents(); fetchBlockedWallets(); }
  }, [tab, token]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── Actions ──────────────────────────────────────────────────────────── */
  const handleUpdateStatus = useCallback(async () => {
    if (!updateTarget) return;
    try {
      await apiFetch(`/admin/transaction/${updateTarget}/status`, { method: "POST", body: JSON.stringify({ status: updateStatus, note: updateNote }) });
      setUpdateMsg(`Updated → ${updateStatus}`); setUpdateTarget(null); fetchTxns(txOffset);
    } catch (e) { setUpdateMsg(`Error: ${e.message}`); }
  }, [apiFetch, updateTarget, updateStatus, updateNote, fetchTxns, txOffset]);

  // Build tax recipients array from % rows — includes purpose label
  const buildTaxRecipients = (rows) => rows.filter((r) => r.wallet && r.pct).map((r) => ({
    wallet: r.wallet.trim(),
    bps: Math.round(parseFloat(r.pct) * 100), // % → bps
    purpose: r.purpose || "other",
  }));

  // Compute effective on-chain tax bps from mode + rate fields
  const effectiveTaxBps = (form) => {
    const { taxMode, taxPct, buyTaxPct, sellTaxPct } = form;
    if (taxMode === "all")          return Math.round(parseFloat(taxPct || "0") * 100);
    if (taxMode === "buy")          return Math.round(parseFloat(buyTaxPct || "0") * 100);
    if (taxMode === "sell")         return Math.round(parseFloat(sellTaxPct || "0") * 100);
    if (taxMode === "differential") return Math.max(
      Math.round(parseFloat(buyTaxPct || "0") * 100),
      Math.round(parseFloat(sellTaxPct || "0") * 100),
    );
    return 0;
  };

  const validateSourceAddr = (chain, addr) => {
    if (!addr) return "Source contract address is required.";
    const evmChains = ["ETH", "BSC", "RH", "BESC"];
    if (evmChains.includes(chain)) {
      if (!/^0x[0-9a-fA-F]{40}$/.test(addr)) return "Invalid EVM source address (must be 0x + 40 hex chars).";
    } else if (chain === "SOL") {
      if (!/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(addr)) return "Invalid Solana source address (base58, 32–44 chars).";
    } else if (chain === "XRP") {
      if (!/^r[1-9A-HJ-NP-Za-km-z]{24,34}$/.test(addr)) return "Invalid XRP source address (starts with r).";
    }
    return null;
  };

  const handleAddToken = useCallback(async () => {
    const { tokenName, symbol, decimals, sourceChain, sourceAddress, bridgeFeeBps, notes, managerAddress,
            existingWrapped, taxMode, buyTaxPct, sellTaxPct, rewardsTokenAddress, lpPairedToken, lpTaxPct,
            swapbackEnabled, swapbackPayoutType, swapbackRouter, swapbackTokenOut, swapbackThreshold } = addForm;
    if (!tokenName || !symbol || !decimals || !sourceChain || !sourceAddress) { setAddMsg("Fill in all required fields."); setAddStatus("error"); return; }

    const addrErr = validateSourceAddr(sourceChain, sourceAddress);
    if (addrErr) { setAddMsg(addrErr); setAddStatus("error"); return; }
    if (addMode === "register" && !/^0x[0-9a-fA-F]{40}$/.test(existingWrapped)) { setAddMsg("Invalid BESC wrapped address."); setAddStatus("error"); return; }

    const taxBpsNum = effectiveTaxBps(addForm);
    const recipients = taxBpsNum > 0 ? buildTaxRecipients(taxRows) : [];
    if (taxBpsNum > 0) {
      const total = recipients.reduce((s, r) => s + r.bps, 0);
      if (recipients.length === 0) { setAddMsg("Add at least one tax recipient wallet."); setAddStatus("error"); return; }
      if (total !== 10000) { setAddMsg(`Tax wallet shares must total 100% (currently ${(total/100).toFixed(2)}%)`); setAddStatus("error"); return; }
    }

    // ERC20 rewards payout: rewardsTokenAddress IS the swapback output token
    const isErc20Payout = swapbackEnabled && swapbackPayoutType === "erc20";
    const finalRewardsTokenAddress = isErc20Payout ? rewardsTokenAddress : null;
    const finalSwapbackTokenOut = isErc20Payout ? rewardsTokenAddress : swapbackTokenOut;

    if (isErc20Payout && (!rewardsTokenAddress || !/^0x[0-9a-fA-F]{40}$/.test(rewardsTokenAddress))) {
      setAddMsg("Rewards token address is required for ERC20 payout — enter the token contract address on BESC."); setAddStatus("error"); return;
    }
    if (swapbackEnabled && (!swapbackRouter || !/^0x[0-9a-fA-F]{40}$/.test(swapbackRouter))) {
      setAddMsg("DEX Router must be a valid 0x address on BESC when auto-convert is enabled."); setAddStatus("error"); return;
    }
    if (swapbackEnabled && swapbackPayoutType === "native" && (!swapbackTokenOut || !/^0x[0-9a-fA-F]{40}$/.test(swapbackTokenOut))) {
      setAddMsg("Output Token (WBESC address) is required for native BESC payout — enter the WBESC contract address on BESC."); setAddStatus("error"); return;
    }
    if (swapbackEnabled && (!swapbackThreshold || parseFloat(swapbackThreshold) <= 0)) {
      setAddMsg("Swapback trigger threshold must be greater than 0."); setAddStatus("error"); return;
    }
    if (lpPairedToken && !/^0x[0-9a-fA-F]{40}$/.test(lpPairedToken)) { setAddMsg("Invalid LP paired token address."); setAddStatus("error"); return; }

    const lpTaxBpsNum = Math.round(parseFloat(lpTaxPct || "0") * 100);
    if (lpTaxBpsNum > 0 && (!lpPairedToken || !/^0x[0-9a-fA-F]{40}$/.test(lpPairedToken))) {
      setAddMsg("LP Paired Token address is required when LP Tax % is set."); setAddStatus("error"); return;
    }

    setAddStatus("deploying"); setAddResult(null);
    setAddMsg(addMode === "deploy" ? "Deploying wrapped contract on BESC — 30–60 seconds…" : "Registering token…");
    try {
      const apiToken = `W${symbol.toUpperCase().replace(/^W/, "")}`;
      const endpoint = addMode === "deploy" ? `/admin/tokens/${apiToken}/deploy` : `/admin/tokens/register`;
      const buyBps = Math.round(parseFloat(buyTaxPct || "0") * 100);
      const sellBps = Math.round(parseFloat(sellTaxPct || "0") * 100);
      const body = addMode === "deploy"
        ? {
            tokenName, symbol, decimals: parseInt(decimals), sourceChain, sourceAddress,
            bridgeFeeBps: parseInt(bridgeFeeBps), notes, managerAddress: managerAddress || undefined,
            taxBps: taxBpsNum, taxRecipients: recipients,
            taxMode,
            buyTaxBps: buyBps,
            sellTaxBps: sellBps,
            rewardsTokenAddress: finalRewardsTokenAddress,
            lpPairedToken: lpPairedToken || null,
            lpTaxBps: lpTaxBpsNum,
            swapbackEnabled: Boolean(swapbackEnabled),
            swapbackRouter: swapbackEnabled ? swapbackRouter : undefined,
            swapbackTokenOut: swapbackEnabled ? finalSwapbackTokenOut : undefined,
            swapbackThreshold: swapbackEnabled ? swapbackThreshold : "0",
          }
        : { tokenName, symbol, decimals: parseInt(decimals), sourceChain, sourceAddress, bescWrappedAddress: existingWrapped, bridgeFeeBps: parseInt(bridgeFeeBps), notes };
      const j = await apiFetch(endpoint, { method: "POST", body: JSON.stringify(body) });
      setAddResult(j); setAddStatus("done");
      setAddMsg(addMode === "deploy" ? `Wrapped deployed at ${j.contractAddress}` : `Registered as ${apiToken}`);
      fetchCustomTokens();
    } catch (e) { setAddStatus("error"); setAddMsg(`Error: ${e.message}`); }
  }, [addForm, addMode, taxRows, apiFetch, fetchCustomTokens]);

  /* ─── Manage token (on-chain editor) ──────────────────────────────────── */
  const openManage = useCallback(async (t) => {
    setManageToken(t); setOnchainConfig(null); setManageMsg(""); setManageLoading(true);
    try {
      const cfg = await apiFetch(`/admin/tokens/${t.api_token}/onchain`);
      setOnchainConfig(cfg);
      setEditTaxBps(String(cfg.transferTaxBps || 0));
      setEditTaxBuyBps(String(cfg.buyTaxBps || 0));
      setEditTaxSellBps(String(cfg.sellTaxBps || 0));
      setEditTaxRows(cfg.taxRecipients?.length > 0
        ? cfg.taxRecipients.map((r) => ({ wallet: r.wallet, pct: String((r.bps / 100).toFixed(2)) }))
        : [{ wallet: "", pct: "" }]);
      setEditSwapback({ enabled: cfg.swapbackEnabled, toNative: cfg.swapbackToNative !== false, router: cfg.swapbackRouter || "", tokenOut: cfg.swapbackTokenOut || "", threshold: cfg.swapbackThreshold || "" });
      setEditManager(cfg.taxManager || "");
      setExemptWallet("");
      setAmmPairInput("");
      setEditLPConfig({ lpBps: String(cfg.lpTaxBps || 0), pairedToken: cfg.lpPairedToken && cfg.lpPairedToken !== "0x0000000000000000000000000000000000000000" ? cfg.lpPairedToken : "" });
    } catch (e) { setManageMsg(`Failed to load on-chain config: ${e.message}`); setManageMsgType("error"); }
    finally { setManageLoading(false); }
  }, [apiFetch]);

  const handleUpdateTax = useCallback(async () => {
    const bpsNum = parseInt(editTaxBps) || 0;
    const buyBpsNum = parseInt(editTaxBuyBps) || 0;
    const sellBpsNum = parseInt(editTaxSellBps) || 0;
    const effectiveTax = Math.max(bpsNum, buyBpsNum, sellBpsNum);
    const recipients = effectiveTax > 0 ? buildTaxRecipients(editTaxRows) : [];
    if (effectiveTax > 0) {
      const total = recipients.reduce((s, r) => s + r.bps, 0);
      if (total !== 10000) { setManageMsg(`Tax % must add to 100% (got ${(total/100).toFixed(2)}%)`); setManageMsgType("error"); return; }
    }
    setManageLoading(true); setManageMsg("");
    try {
      const j = await apiFetch(`/admin/tokens/${manageToken.api_token}/taxconfig`, { method: "POST", body: JSON.stringify({ taxBps: bpsNum, buyTaxBps: buyBpsNum, sellTaxBps: sellBpsNum, taxRecipients: recipients }) });
      setManageMsg(`Tax updated — tx ${shortHash(j.hash)}`); setManageMsgType("success");
      openManage(manageToken);
    } catch (e) { setManageMsg(e.message); setManageMsgType("error"); }
    finally { setManageLoading(false); }
  }, [editTaxBps, editTaxBuyBps, editTaxSellBps, editTaxRows, manageToken, apiFetch, openManage]);

  const handleUpdateSwapback = useCallback(async () => {
    setManageLoading(true); setManageMsg("");
    try {
      const j = await apiFetch(`/admin/tokens/${manageToken.api_token}/swapback`, { method: "POST", body: JSON.stringify({ enabled: editSwapback.enabled, toNative: editSwapback.toNative, router: editSwapback.router, tokenOut: editSwapback.tokenOut, threshold: editSwapback.threshold }) });
      setManageMsg(`Swapback updated — tx ${shortHash(j.hash)}`); setManageMsgType("success");
    } catch (e) { setManageMsg(e.message); setManageMsgType("error"); }
    finally { setManageLoading(false); }
  }, [editSwapback, manageToken, apiFetch]);

  const handleSetAMMPair = useCallback(async (value) => {
    if (!/^0x[0-9a-fA-F]{40}$/.test(ammPairInput.trim())) { setManageMsg("Invalid pair address"); setManageMsgType("error"); return; }
    setManageLoading(true); setManageMsg("");
    try {
      const j = await apiFetch(`/admin/tokens/${manageToken.api_token}/ammpair`, { method: "POST", body: JSON.stringify({ pair: ammPairInput.trim(), value }) });
      setManageMsg(`AMM pair ${value ? "registered" : "removed"} — tx ${shortHash(j.hash)}`); setManageMsgType("success");
      setAmmPairInput("");
    } catch (e) { setManageMsg(e.message); setManageMsgType("error"); }
    finally { setManageLoading(false); }
  }, [ammPairInput, manageToken, apiFetch]);

  const handleUpdateLPConfig = useCallback(async () => {
    setManageLoading(true); setManageMsg("");
    try {
      const j = await apiFetch(`/admin/tokens/${manageToken.api_token}/lpconfig`, { method: "POST", body: JSON.stringify({ lpBps: parseInt(editLPConfig.lpBps) || 0, pairedToken: editLPConfig.pairedToken || null }) });
      setManageMsg(`LP config updated — tx ${shortHash(j.hash)}`); setManageMsgType("success");
      openManage(manageToken);
    } catch (e) { setManageMsg(e.message); setManageMsgType("error"); }
    finally { setManageLoading(false); }
  }, [editLPConfig, manageToken, apiFetch, openManage]);

  const handleUpdateManager = useCallback(async () => {
    setManageLoading(true); setManageMsg("");
    try {
      const j = await apiFetch(`/admin/tokens/${manageToken.api_token}/taxmanager`, { method: "POST", body: JSON.stringify({ manager: editManager }) });
      setManageMsg(`Tax manager updated — tx ${shortHash(j.hash)}`); setManageMsgType("success");
      openManage(manageToken);
    } catch (e) { setManageMsg(e.message); setManageMsgType("error"); }
    finally { setManageLoading(false); }
  }, [editManager, manageToken, apiFetch, openManage]);

  const handleSetExempt = useCallback(async (exempt) => {
    if (!/^0x[0-9a-fA-F]{40}$/.test(exemptWallet.trim())) { setManageMsg("Invalid wallet address"); setManageMsgType("error"); return; }
    setManageLoading(true); setManageMsg("");
    try {
      const j = await apiFetch(`/admin/tokens/${manageToken.api_token}/taxexempt`, { method: "POST", body: JSON.stringify({ wallet: exemptWallet.trim(), exempt }) });
      setManageMsg(`Tax exempt=${exempt} set for ${shortAddr(exemptWallet)} — tx ${shortHash(j.hash)}`); setManageMsgType("success");
      setExemptWallet("");
    } catch (e) { setManageMsg(e.message); setManageMsgType("error"); }
    finally { setManageLoading(false); }
  }, [exemptWallet, manageToken, apiFetch]);

  /* ─── Support ticket handlers ──────────────────────────────────────── */
  const fetchSupportTickets = useCallback(async (filter) => {
    setSupportLoading(true); setSupportMsg("");
    const f = filter ?? supportFilter;
    try {
      const j = await apiFetch(`/admin/support?status=${f}`);
      setSupportTickets(j.tickets || []);
    } catch (e) { setSupportMsg(e.message); setSupportMsgType("error"); }
    finally { setSupportLoading(false); }
  }, [apiFetch, supportFilter]);

  const handleResolveTicket = useCallback(async (id) => {
    setSupportLoading(true); setSupportMsg("");
    try {
      const notes = supportNotesDraft.trim() || undefined;
      await apiFetch(`/admin/support/${id}/resolve`, { method: "POST", body: JSON.stringify({ notes }) });
      setSupportSelected(null); setSupportNotesDraft("");
      setSupportMsg("Ticket resolved."); setSupportMsgType("success");
      fetchSupportTickets();
    } catch (e) { setSupportMsg(e.message); setSupportMsgType("error"); }
    finally { setSupportLoading(false); }
  }, [apiFetch, supportNotesDraft, fetchSupportTickets]);

  const handleReopenTicket = useCallback(async (id) => {
    setSupportLoading(true); setSupportMsg("");
    try {
      await apiFetch(`/admin/support/${id}/reopen`, { method: "POST", body: JSON.stringify({}) });
      setSupportSelected(null);
      setSupportMsg("Ticket reopened."); setSupportMsgType("success");
      fetchSupportTickets();
    } catch (e) { setSupportMsg(e.message); setSupportMsgType("error"); }
    finally { setSupportLoading(false); }
  }, [apiFetch, fetchSupportTickets]);

  const handleSaveNotes = useCallback(async (id) => {
    try {
      await apiFetch(`/admin/support/${id}/notes`, { method: "POST", body: JSON.stringify({ notes: supportNotesDraft }) });
      setSupportMsg("Notes saved."); setSupportMsgType("success");
    } catch (e) { setSupportMsg(e.message); setSupportMsgType("error"); }
  }, [apiFetch, supportNotesDraft]);

  const handleDeleteTicket = useCallback(async (id) => {
    if (!window.confirm("Delete this ticket permanently?")) return;
    setSupportLoading(true); setSupportMsg("");
    try {
      await apiFetch(`/admin/support/${id}/delete`, { method: "POST", body: JSON.stringify({}) });
      setSupportSelected(null); setSupportNotesDraft("");
      fetchSupportTickets();
    } catch (e) { setSupportMsg(e.message); setSupportMsgType("error"); }
    finally { setSupportLoading(false); }
  }, [apiFetch, fetchSupportTickets]);

  const handleToggleToken = useCallback(async (apiToken, enable) => {
    try {
      await apiFetch(`/admin/tokens/${apiToken}/enable`, { method: "POST", body: JSON.stringify({ enabled: enable }) });
      fetchCustomTokens();
    } catch (e) { setPageErr(e.message); }
  }, [apiFetch, fetchCustomTokens]);

  const handleBlock = useCallback(async (addr, reason) => {
    const address = (addr || blockInput).trim();
    const why = (reason || blockReason).trim() || "Manually blocked by admin";
    if (!/^0x[0-9a-fA-F]{40}$/.test(address)) { setSecMsg("Invalid EVM address"); setSecMsgType("error"); return; }
    try {
      await apiFetch("/admin/security/block", { method: "POST", body: JSON.stringify({ address, reason: why }) });
      setSecMsg(`Blocked ${shortAddr(address)}`); setSecMsgType("success");
      setBlockInput(""); setBlockReason("");
      fetchBlockedWallets(); fetchSecEvents();
    } catch (e) { setSecMsg(e.message); setSecMsgType("error"); }
  }, [apiFetch, blockInput, blockReason, fetchBlockedWallets, fetchSecEvents]);

  const handleUnblock = useCallback(async (address) => {
    try {
      await apiFetch(`/admin/security/block/${address}`, { method: "DELETE" });
      setSecMsg(`Unblocked ${shortAddr(address)}`); setSecMsgType("success");
      fetchBlockedWallets();
    } catch (e) { setSecMsg(e.message); setSecMsgType("error"); }
  }, [apiFetch, fetchBlockedWallets]);

  /* ─── TABS definition ──────────────────────────────────────────────────── */
  const TABS = [
    { key: "dashboard", label: "Dashboard", icon: "◈" },
    { key: "transactions", label: "Transactions", icon: "⇄" },
    { key: "assets", label: "Assets", icon: "◎" },
    { key: "addtoken", label: "Add Token", icon: "+" },
    { key: "support", label: "Support", icon: "✉" },
    { key: "security", label: "Security", icon: "⚑" },
  ];

  /* ─── LOGIN SCREEN ──────────────────────────────────────────────────────── */
  if (!token) {
    return (
      <>
        <APStyles />
        <div className="ap-overlay">
          <div className="ap-login-box">
            <div className="ap-login-logo">
              <div className="ap-login-chain-name">BESC</div>
              <div className="ap-login-sub-text">Bridge Admin</div>
            </div>
            <form onSubmit={handleLogin} className="ap-login-form">
              <label className="ap-field-label">
                Email
                <input className="ap-field-input" type="email" autoComplete="username" placeholder="support@besc.services" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required />
              </label>
              <label className="ap-field-label">
                Password
                <input className="ap-field-input" type="password" autoComplete="current-password" placeholder="••••••••••" value={loginPass} onChange={(e) => setLoginPass(e.target.value)} required />
              </label>
              {loginErr && <Alert type="error">{loginErr}</Alert>}
              <button className="ap-btn ap-btn-primary ap-btn-block" type="submit" disabled={loginLoading}>
                {loginLoading ? "Signing in…" : "Sign In"}
              </button>
            </form>
            <p className="ap-login-note">Admin access only · All activity is logged</p>
          </div>
        </div>
      </>
    );
  }

  /* ─── MAIN PANEL ─────────────────────────────────────────────────────────── */
  return (
    <>
      <APStyles />
      <div className="ap-overlay ap-panel">

        {/* ── Sidebar (desktop) ─────────────────────────────────────────── */}
        <aside className="ap-sidebar">
          <div className="ap-sidebar-brand">
            <span className="ap-brand-name">BESC</span>
            <span className="ap-brand-sub">Admin</span>
          </div>
          <nav className="ap-sidebar-nav">
            {TABS.map(({ key, label, icon }) => (
              <button key={key} className={`ap-side-tab${tab === key ? " ap-side-tab--active" : ""}`} onClick={() => setTab(key)}>
                <span className="ap-side-icon">{icon}</span>
                <span>{label}</span>
              </button>
            ))}
          </nav>
          <div className="ap-sidebar-footer">
            <button className="ap-btn ap-btn-ghost ap-btn-sm" onClick={logout}>Log out</button>
          </div>
        </aside>

        {/* ── Content area ──────────────────────────────────────────────── */}
        <div className="ap-content-wrap">

          {/* Top bar (mobile only) */}
          <div className="ap-topbar">
            <span className="ap-topbar-title">BESC Admin</span>
            <button className="ap-btn ap-btn-ghost ap-btn-sm" onClick={logout}>Log out</button>
          </div>

          {/* Bottom tab bar (mobile only) */}
          <nav className="ap-bottombar">
            {TABS.map(({ key, label, icon }) => (
              <button key={key} className={`ap-bottom-tab${tab === key ? " ap-bottom-tab--active" : ""}`} onClick={() => setTab(key)}>
                <span className="ap-bottom-icon">{icon}</span>
                <span className="ap-bottom-label">{label}</span>
              </button>
            ))}
          </nav>

          {/* Main scroll area */}
          <main className="ap-main-scroll">
            {pageErr && <Alert type="error" onClose={() => setPageErr("")}>{pageErr}</Alert>}
            {loading && <Spinner />}

            {/* ── DASHBOARD ───────────────────────────────────────────── */}
            {tab === "dashboard" && stats && (
              <div>
                <div className="ap-page-head">
                  <h1 className="ap-page-title">Dashboard</h1>
                  <button className="ap-btn ap-btn-sm" onClick={fetchStats}>Refresh</button>
                </div>
                <div className="ap-stat-grid">
                  <div className="ap-stat-card ap-stat-card--highlight">
                    <div className="ap-stat-label">Last 24h</div>
                    <div className="ap-stat-num">{stats.last24h}</div>
                  </div>
                  {stats.byStatus.map((r) => (
                    <div key={r.status} className="ap-stat-card">
                      <div className="ap-stat-label" style={{ color: statusColor(r.status) }}>{r.status}</div>
                      <div className="ap-stat-num" style={{ color: statusColor(r.status) }}>{r.count}</div>
                    </div>
                  ))}
                </div>

                <h2 className="ap-section-title">Top Routes</h2>
                <div className="ap-route-list">
                  {stats.byRoute.slice(0, 8).map((r, i) => (
                    <div key={i} className="ap-route-row">
                      <span className="ap-route-chain">{r.source_chain}</span>
                      <span className="ap-route-arrow">→</span>
                      <span className="ap-route-chain">{r.destination_chain}</span>
                      <span className="ap-route-count">{r.count} txns</span>
                    </div>
                  ))}
                </div>

                <h2 className="ap-section-title">By Token</h2>
                <div className="ap-chip-row">
                  {stats.byToken.map((r) => (
                    <div key={r.token} className="ap-chip">
                      <strong>{r.token}</strong>
                      <span>{r.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── TRANSACTIONS ─────────────────────────────────────────── */}
            {tab === "transactions" && (
              <div>
                <h1 className="ap-page-title">Transactions</h1>

                <div className="ap-filter-bar">
                  <select className="ap-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="all">All statuses</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select className="ap-select" value={filterSrc} onChange={(e) => setFilterSrc(e.target.value)}>
                    <option value="all">Any source</option>
                    {ALL_CHAINS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select className="ap-select" value={filterDest} onChange={(e) => setFilterDest(e.target.value)}>
                    <option value="all">Any dest</option>
                    {ALL_CHAINS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="ap-filter-bar ap-filter-bar--search">
                  <input className="ap-field-input ap-search-input" placeholder="Search hash or address…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchTxns(0)} />
                  <button className="ap-btn" onClick={() => fetchTxns(0)}>Search</button>
                </div>

                {updateMsg && <Alert type={updateMsg.startsWith("Error") ? "error" : "success"} onClose={() => setUpdateMsg("")}>{updateMsg}</Alert>}

                {updateTarget && (
                  <div className="ap-update-row">
                    <div className="ap-update-label">Update <code>{shortHash(updateTarget)}</code></div>
                    <div className="ap-update-controls">
                      <select className="ap-select" value={updateStatus} onChange={(e) => setUpdateStatus(e.target.value)}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <input className="ap-field-input" placeholder="Note (optional)" value={updateNote} onChange={(e) => setUpdateNote(e.target.value)} />
                      <button className="ap-btn ap-btn-primary" onClick={handleUpdateStatus}>Confirm</button>
                      <button className="ap-btn ap-btn-ghost" onClick={() => { setUpdateTarget(null); setUpdateMsg(""); }}>Cancel</button>
                    </div>
                  </div>
                )}

                {/* Desktop table */}
                <div className="ap-table-wrap ap-desktop-only">
                  <table className="ap-table">
                    <thead>
                      <tr><th>Time</th><th>Status</th><th>Route</th><th>Type</th><th>Amount</th><th>User</th><th>Src Tx</th><th>Dst Tx</th><th></th></tr>
                    </thead>
                    <tbody>
                      {txns.map((t) => (
                        <tr key={t.tx_hash}>
                          <td>{fmt(t.created_at)}</td>
                          <td><StatusPill status={t.status} /></td>
                          <td>{t.source_chain} → {t.destination_chain}</td>
                          <td>{(t.type || "").toUpperCase()}</td>
                          <td>{t.gross_amount || t.amount || "—"}</td>
                          <td title={t.user}>{shortAddr(t.user)}</td>
                          <td title={t.tx_hash}>{shortHash(t.tx_hash)}</td>
                          <td title={t.destination_tx_hash || ""}>{shortHash(t.destination_tx_hash)}</td>
                          <td><button className="ap-btn ap-btn-sm" onClick={() => { setUpdateTarget(t.tx_hash); setUpdateNote(t.destination_last_error || ""); setUpdateMsg(""); }}>Edit</button></td>
                        </tr>
                      ))}
                      {txns.length === 0 && !loading && <tr><td colSpan={9} className="ap-empty">No transactions found</td></tr>}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="ap-mobile-only ap-tx-cards">
                  {txns.map((t) => (
                    <div key={t.tx_hash} className="ap-tx-card">
                      <div className="ap-tx-card-head">
                        <StatusPill status={t.status} />
                        <span className="ap-tx-card-route">{t.source_chain} → {t.destination_chain}</span>
                        <span className="ap-tx-card-time">{fmt(t.created_at)}</span>
                      </div>
                      <div className="ap-tx-card-row">
                        <span className="ap-tx-card-key">Type</span>
                        <span>{(t.type || "—").toUpperCase()}</span>
                      </div>
                      <div className="ap-tx-card-row">
                        <span className="ap-tx-card-key">Amount</span>
                        <span>{t.gross_amount || t.amount || "—"}</span>
                      </div>
                      <div className="ap-tx-card-row">
                        <span className="ap-tx-card-key">User</span>
                        <span className="ap-mono" title={t.user}>{shortAddr(t.user)}</span>
                      </div>
                      <div className="ap-tx-card-row">
                        <span className="ap-tx-card-key">Src Tx</span>
                        <span className="ap-mono" title={t.tx_hash}>{shortHash(t.tx_hash)}</span>
                      </div>
                      {t.destination_tx_hash && (
                        <div className="ap-tx-card-row">
                          <span className="ap-tx-card-key">Dst Tx</span>
                          <span className="ap-mono">{shortHash(t.destination_tx_hash)}</span>
                        </div>
                      )}
                      {t.destination_last_error && (
                        <div className="ap-tx-card-error">{t.destination_last_error}</div>
                      )}
                      <div className="ap-tx-card-action">
                        <button className="ap-btn ap-btn-sm" onClick={() => { setUpdateTarget(t.tx_hash); setUpdateNote(t.destination_last_error || ""); setUpdateMsg(""); }}>Edit Status</button>
                      </div>
                    </div>
                  ))}
                  {txns.length === 0 && !loading && <div className="ap-empty">No transactions found</div>}
                </div>

                <div className="ap-pagination">
                  <span className="ap-muted-text">{txOffset + 1}–{Math.min(txOffset + PAGE_SIZE, txTotal)} of {txTotal}</span>
                  <button className="ap-btn ap-btn-sm" disabled={txOffset === 0} onClick={() => fetchTxns(Math.max(0, txOffset - PAGE_SIZE))}>← Prev</button>
                  <button className="ap-btn ap-btn-sm" disabled={txOffset + PAGE_SIZE >= txTotal} onClick={() => fetchTxns(txOffset + PAGE_SIZE)}>Next →</button>
                </div>
              </div>
            )}

            {/* ── ACTIVE ASSETS ────────────────────────────────────────── */}
            {tab === "assets" && (
              <div>
                <div className="ap-page-head">
                  <h1 className="ap-page-title">Active Assets</h1>
                  <button className="ap-btn ap-btn-sm" onClick={fetchAssets}>Refresh</button>
                </div>
                <p className="ap-muted-text">Wrapped assets deployed via the listing approval system.</p>

                {/* Desktop table */}
                <div className="ap-table-wrap ap-desktop-only">
                  <table className="ap-table">
                    <thead><tr><th>Token</th><th>Symbol</th><th>Chain</th><th>Source</th><th>BESC Wrapped</th><th>Routes</th><th>Approved</th></tr></thead>
                    <tbody>
                      {assets.map((a) => (
                        <tr key={a.id}>
                          <td>{a.token_name}</td><td>{a.symbol}</td><td>{a.chain}</td>
                          <td title={a.token_address}>{shortAddr(a.token_address)}</td>
                          <td title={a.besc_wrapped_address}>{shortAddr(a.besc_wrapped_address)}</td>
                          <td>{a.routes ? (() => { try { return JSON.parse(a.routes).join(", "); } catch { return a.routes; } })() : "—"}</td>
                          <td>{fmt(a.approved_at)}</td>
                        </tr>
                      ))}
                      {assets.length === 0 && !loading && <tr><td colSpan={7} className="ap-empty">No active assets yet</td></tr>}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="ap-mobile-only">
                  {assets.map((a) => (
                    <div key={a.id} className="ap-tx-card">
                      <div className="ap-tx-card-head">
                        <strong>{a.token_name}</strong>
                        <span className="ap-pill" style={{ color: "#4ecb71", borderColor: "#4ecb71" }}>{a.symbol}</span>
                        <span className="ap-muted-text">{a.chain}</span>
                      </div>
                      <div className="ap-tx-card-row"><span className="ap-tx-card-key">Source</span><span className="ap-mono">{shortAddr(a.token_address)}</span></div>
                      <div className="ap-tx-card-row"><span className="ap-tx-card-key">BESC Wrapped</span><span className="ap-mono">{shortAddr(a.besc_wrapped_address)}</span></div>
                      <div className="ap-tx-card-row"><span className="ap-tx-card-key">Approved</span><span>{fmt(a.approved_at)}</span></div>
                    </div>
                  ))}
                  {assets.length === 0 && !loading && <div className="ap-empty">No active assets yet</div>}
                </div>

                <div className="ap-info-box" style={{ marginTop: 20 }}>
                  <strong>To add an asset:</strong> use <button className="ap-inline-link" onClick={() => setTab("addtoken")}>Add Token</button> to deploy &amp; register instantly, or use Listings for full vetting.
                </div>
              </div>
            )}

            {/* ── ADD TOKEN ────────────────────────────────────────────── */}
            {tab === "addtoken" && !manageToken && (
              <div>
                <h1 className="ap-page-title">Token Management</h1>

                {customTokens.length > 0 && (
                  <>
                    <h2 className="ap-section-title">Registered Tokens</h2>
                    <div className="ap-token-list">
                      {customTokens.map((t) => (
                        <div key={t.id} className="ap-token-card">
                          <div className="ap-token-card-head">
                            <strong>{t.api_token}</strong>
                            <span className="ap-muted-text">{t.symbol} · {t.source_chain}</span>
                            <StatusPill status={t.enabled ? "live" : "disabled"} />
                          </div>
                          <div className="ap-tx-card-row"><span className="ap-tx-card-key">Source</span><span className="ap-mono">{shortAddr(t.source_address)}</span></div>
                          <div className="ap-tx-card-row">
                            <span className="ap-tx-card-key">BESC Wrapped</span>
                            <span className="ap-mono">{t.besc_wrapped_address ? shortAddr(t.besc_wrapped_address) : <span className="ap-muted-text">not deployed</span>}</span>
                          </div>
                          <div className="ap-tx-card-row"><span className="ap-tx-card-key">Tax</span><span>{t.tax_bps ? `${(t.tax_bps/100).toFixed(2)}%` : "None"}</span></div>
                          <div className="ap-tx-card-row"><span className="ap-tx-card-key">Swapback</span><span>{t.swapback_enabled ? "Enabled" : "Off"}</span></div>
                          <div className="ap-token-card-action" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {t.besc_wrapped_address && (
                              <>
                                <button className={`ap-btn ap-btn-sm ${t.enabled ? "ap-btn-danger" : "ap-btn-success"}`} onClick={() => handleToggleToken(t.api_token, !t.enabled)}>
                                  {t.enabled ? "Disable" : "Enable"}
                                </button>
                                <button className="ap-btn ap-btn-sm" onClick={() => openManage(t)}>⚙ Manage</button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
                  <h2 className="ap-section-title" style={{ margin: 0 }}>Add New Token</h2>
                  <Link
                    to="/admin/guide"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: "rgba(212,168,67,0.08)", border: "1px solid rgba(212,168,67,0.25)",
                      color: "#d4a843", borderRadius: 8, padding: "5px 12px",
                      fontSize: 13, fontWeight: 600, textDecoration: "none",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "rgba(212,168,67,0.16)"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "rgba(212,168,67,0.08)"}
                  >
                    📋 How-to Guide
                  </Link>
                </div>
                <div className="ap-toggle-row">
                  <button className={`ap-btn${addMode === "deploy" ? " ap-btn-primary" : " ap-btn-ghost"}`} onClick={() => setAddMode("deploy")}>Deploy Wrapped Contract</button>
                  <button className={`ap-btn${addMode === "register" ? " ap-btn-primary" : " ap-btn-ghost"}`} onClick={() => setAddMode("register")}>Register Existing</button>
                </div>
                <p className="ap-muted-text" style={{ marginBottom: 16 }}>
                  {addMode === "deploy" ? "Deploys a BridgeWrappedToken on BESC with tax/swapback config, then registers it." : "Register a wrapped contract you've already deployed on BESC."}
                </p>

                <div className="ap-form-stack">
                  <div className="ap-form-row">
                    <label className="ap-field-label">Token Name *<input className="ap-field-input" placeholder="e.g. Shiba Inu" value={addForm.tokenName} onChange={(e) => setAddForm((f) => ({ ...f, tokenName: e.target.value }))} /></label>
                    <label className="ap-field-label">Symbol *<input className="ap-field-input" placeholder="e.g. SHIB" value={addForm.symbol} onChange={(e) => setAddForm((f) => ({ ...f, symbol: e.target.value.toUpperCase() }))} /></label>
                  </div>
                  <div className="ap-form-row">
                    <label className="ap-field-label">Decimals *<input className="ap-field-input" type="number" min="0" max="18" placeholder="18" value={addForm.decimals} onChange={(e) => setAddForm((f) => ({ ...f, decimals: e.target.value }))} /></label>
                    <label className="ap-field-label">Source Chain *
                      <select className="ap-select ap-select-full" value={addForm.sourceChain} onChange={(e) => setAddForm((f) => ({ ...f, sourceChain: e.target.value }))}>
                        {SOURCE_CHAINS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </label>
                  </div>
                  <label className="ap-field-label">Source Contract Address * (on {addForm.sourceChain})<input className="ap-field-input" placeholder="0x…" value={addForm.sourceAddress} onChange={(e) => setAddForm((f) => ({ ...f, sourceAddress: e.target.value.trim() }))} /></label>
                  {addMode === "register" && (
                    <label className="ap-field-label">Existing BESC Wrapped Contract *<input className="ap-field-input" placeholder="0x…" value={addForm.existingWrapped} onChange={(e) => setAddForm((f) => ({ ...f, existingWrapped: e.target.value.trim() }))} /></label>
                  )}
                  {addMode === "deploy" && (
                    <>
                      <label className="ap-field-label">Team Manager Wallet (gets tax-update rights — optional)<input className="ap-field-input" placeholder="0x… — team's wallet address or leave blank" value={addForm.managerAddress} onChange={(e) => setAddForm((f) => ({ ...f, managerAddress: e.target.value.trim() }))} /></label>

                      {/* ── Tax config ─────────────────────────────────── */}
                      <div className="ap-subsection">
                        <div className="ap-subsection-head">
                          <span>Tax Configuration</span>
                        </div>

                        {/* Tax mode */}
                        <div>
                          <div className="ap-subsection-label">When does tax apply?</div>
                          <div className="ap-tax-mode-row">
                            {TAX_MODES.map((m) => (
                              <button key={m.key} className={`ap-btn ap-btn-sm${addForm.taxMode === m.key ? " ap-btn-primary" : " ap-btn-ghost"}`}
                                onClick={() => setAddForm((f) => ({ ...f, taxMode: m.key }))}>
                                {m.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Rate input(s) */}
                        <div className="ap-form-row">
                          {(addForm.taxMode === "all") && (
                            <label className="ap-field-label">Tax Rate (%)
                              <input className="ap-field-input" type="number" min="0" max="50" step="0.01" placeholder="0" value={addForm.taxPct} onChange={(e) => setAddForm((f) => ({ ...f, taxPct: e.target.value }))} />
                            </label>
                          )}
                          {(addForm.taxMode === "buy" || addForm.taxMode === "differential") && (
                            <label className="ap-field-label">Buy Tax (%)
                              <input className="ap-field-input" type="number" min="0" max="50" step="0.01" placeholder="0" value={addForm.buyTaxPct} onChange={(e) => setAddForm((f) => ({ ...f, buyTaxPct: e.target.value }))} />
                            </label>
                          )}
                          {(addForm.taxMode === "sell" || addForm.taxMode === "differential") && (
                            <label className="ap-field-label">Sell Tax (%)
                              <input className="ap-field-input" type="number" min="0" max="50" step="0.01" placeholder="0" value={addForm.sellTaxPct} onChange={(e) => setAddForm((f) => ({ ...f, sellTaxPct: e.target.value }))} />
                            </label>
                          )}
                        </div>
                        {effectiveTaxBps(addForm) > 0 && (
                          <p className="ap-muted-text" style={{ fontSize: 11, marginBottom: 0 }}>
                            On-chain wrapped contract enforces <strong>{(effectiveTaxBps(addForm)/100).toFixed(2)}%</strong> on all transfers on BESC.
                            Buy/sell-only enforcement requires DEX pair detection in a future contract upgrade.
                          </p>
                        )}

                        {/* Tax recipients */}
                        {effectiveTaxBps(addForm) > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <div className="ap-subsection-label">Where does the tax go? — must total 100%</div>
                            {taxRows.map((row, i) => (
                              <div key={i} className="ap-tax-row" style={{ marginBottom: 6 }}>
                                <select className="ap-select" value={row.purpose} onChange={(e) => setTaxRows((r) => r.map((x, j) => j === i ? { ...x, purpose: e.target.value } : x))} style={{ minWidth: 110 }}>
                                  {TAX_PURPOSES.map((p) => <option key={p} value={p}>{TAX_PURPOSE_LABELS[p]}</option>)}
                                </select>
                                <input className="ap-field-input" placeholder="0x… wallet" value={row.wallet} onChange={(e) => setTaxRows((r) => r.map((x, j) => j === i ? { ...x, wallet: e.target.value.trim() } : x))} style={{ flex: 3 }} />
                                <input className="ap-field-input" type="number" min="0" max="100" step="0.01" placeholder="%" value={row.pct} onChange={(e) => setTaxRows((r) => r.map((x, j) => j === i ? { ...x, pct: e.target.value } : x))} style={{ flex: 1, minWidth: 64 }} />
                                <button className="ap-btn ap-btn-sm ap-btn-danger" onClick={() => setTaxRows((r) => r.filter((_, j) => j !== i))} disabled={taxRows.length <= 1}>−</button>
                              </div>
                            ))}
                            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
                              <button className="ap-btn ap-btn-sm" onClick={() => setTaxRows((r) => [...r, { wallet: "", pct: "", purpose: "team" }])}>+ Add Recipient</button>
                              <span className="ap-muted-text" style={{ fontSize: 11 }}>Total: {taxRows.reduce((s, r) => s + (parseFloat(r.pct)||0), 0).toFixed(2)}%</span>
                            </div>

                            {/* LP config — shown when any recipient has liquidity purpose */}
                            {taxRows.some((r) => r.purpose === "liquidity") && (
                              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
                                <label className="ap-field-label" style={{ flex: 2, minWidth: 200 }}>
                                  LP Paired Token Address on BESC
                                  <span className="ap-muted-text" style={{ fontSize: 11 }}>The other side of the auto-LP pair (e.g. WBESC, USDT)</span>
                                  <input className="ap-field-input" placeholder="0x… paired token on BESC" value={addForm.lpPairedToken} onChange={(e) => setAddForm((f) => ({ ...f, lpPairedToken: e.target.value.trim() }))} />
                                </label>
                                <label className="ap-field-label" style={{ flex: 1, minWidth: 110 }}>
                                  LP Tax Share (%)
                                  <span className="ap-muted-text" style={{ fontSize: 11 }}>% of tax to auto-LP</span>
                                  <input className="ap-field-input" type="number" min="0" max="50" step="0.01" placeholder="e.g. 5" value={addForm.lpTaxPct} onChange={(e) => setAddForm((f) => ({ ...f, lpTaxPct: e.target.value }))} />
                                </label>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* ── Rewards Token (ERC20 payout) ───────────────── */}
                      <div className="ap-subsection" style={{ background: "rgba(212,168,67,0.05)", border: "1px solid rgba(212,168,67,0.18)", borderRadius: 10, padding: "14px 16px" }}>
                        <div className="ap-subsection-head" style={{ marginBottom: 8 }}>
                          <span style={{ color: "#d4a843", fontWeight: 700 }}>Rewards Token Payout</span>
                          <span className="ap-muted-text" style={{ fontSize: 11 }}>The ERC20 token that holders receive when tax is auto-converted and distributed</span>
                        </div>
                        <label className="ap-field-label">
                          <span style={{ color: "#d4a843", fontWeight: 600 }}>Rewards Token Contract Address on BESC</span>
                          <span className="ap-muted-text" style={{ fontSize: 11, marginTop: 2 }}>
                            Enter the contract address of the token to pay out (e.g. WBNB, BUSD, USDT on BESC).
                            Leave blank if paying out in native BESC instead.
                          </span>
                          <input
                            className="ap-field-input"
                            style={{ marginTop: 6 }}
                            placeholder="0x… rewards token contract on BESC  (leave blank for native BESC)"
                            value={addForm.rewardsTokenAddress}
                            onChange={(e) => {
                              const v = e.target.value.trim();
                              setAddForm((f) => ({
                                ...f,
                                rewardsTokenAddress: v,
                                swapbackPayoutType: v ? "erc20" : "native",
                                // filling rewards token requires swapback — auto-enable so it can't be missed
                                swapbackEnabled: v ? true : f.swapbackEnabled,
                              }));
                            }}
                          />
                        </label>
                      </div>

                      {/* ── Auto-Convert / Swapback ────────────────────── */}
                      <div className="ap-subsection">
                        <div className="ap-subsection-head">
                          <span>Auto-Convert (Swapback)</span>
                          <span className="ap-muted-text" style={{ fontSize: 11 }}>Contract swaps collected tax tokens via a DEX and distributes — required for rewards payout.</span>
                        </div>
                        <label className="ap-check-label">
                          <input type="checkbox" checked={addForm.swapbackEnabled} onChange={(e) => setAddForm((f) => ({ ...f, swapbackEnabled: e.target.checked }))} />
                          Enable auto-convert
                        </label>
                        {addForm.swapbackEnabled && (
                          <div className="ap-form-stack" style={{ marginTop: 10 }}>

                            <div className="ap-muted-text" style={{ fontSize: 11, background: "rgba(255,255,255,0.03)", borderRadius: 6, padding: "6px 10px" }}>
                              {addForm.swapbackPayoutType === "erc20"
                                ? `Payout mode: ERC20 Rewards Token — ${addForm.rewardsTokenAddress || "enter address in the Rewards Token section above"}`
                                : "Payout mode: Native BESC — tax swaps to BESC and distributes to recipients"}
                            </div>

                            <label className="ap-field-label">DEX Router on BESC
                              <span className="ap-muted-text" style={{ fontSize: 11, marginLeft: 6 }}>Uniswap V2-compatible router that performs the swap</span>
                              <input className="ap-field-input" placeholder="0x… router on BESC" value={addForm.swapbackRouter} onChange={(e) => setAddForm((f) => ({ ...f, swapbackRouter: e.target.value.trim() }))} />
                            </label>

                            {/* Only show Output Token field for native payout — erc20 uses rewardsTokenAddress */}
                            {addForm.swapbackPayoutType === "native" && (
                              <label className="ap-field-label">Output Token (WBESC or buyback token)
                                <span className="ap-muted-text" style={{ fontSize: 11, marginLeft: 6 }}>The wrapped BESC address or token to buy back</span>
                                <input className="ap-field-input" placeholder="0x… WBESC or output token" value={addForm.swapbackTokenOut} onChange={(e) => setAddForm((f) => ({ ...f, swapbackTokenOut: e.target.value.trim() }))} />
                              </label>
                            )}

                            <label className="ap-field-label">Trigger Threshold (token units)
                              <span className="ap-muted-text" style={{ fontSize: 11, marginLeft: 6 }}>Min accumulated tax tokens before auto-convert fires</span>
                              <input className="ap-field-input" placeholder="e.g. 1000" value={addForm.swapbackThreshold} onChange={(e) => setAddForm((f) => ({ ...f, swapbackThreshold: e.target.value }))} />
                            </label>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  <div className="ap-form-row">
                    <label className="ap-field-label">Bridge Fee (bps)<input className="ap-field-input" type="number" min="0" max="1000" value={addForm.bridgeFeeBps} onChange={(e) => setAddForm((f) => ({ ...f, bridgeFeeBps: e.target.value }))} /></label>
                    <label className="ap-field-label">Notes (internal)<input className="ap-field-input" placeholder="Optional" value={addForm.notes} onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))} /></label>
                  </div>
                </div>

                {addMsg && <Alert type={addStatus === "done" ? "success" : addStatus === "deploying" ? "warn" : "error"}>{addMsg}</Alert>}
                {addResult?.contractAddress && addStatus === "done" && (
                  <div className="ap-info-box">
                    <div><strong>BESC Wrapped:</strong> <code className="ap-mono">{addResult.contractAddress}</code></div>
                    {addResult.deployTxHash && <div style={{ marginTop: 4 }}><strong>Deploy Tx:</strong> <code className="ap-mono">{shortHash(addResult.deployTxHash)}</code></div>}
                    <p className="ap-muted-text" style={{ marginTop: 8, marginBottom: 0 }}>Click <strong>Enable</strong> on the token row above to go live. Use <strong>⚙ Manage</strong> to update tax/swapback settings anytime.</p>
                  </div>
                )}

                <div style={{ marginTop: 20 }}>
                  <button className="ap-btn ap-btn-primary ap-btn-wide" disabled={addStatus === "deploying"} onClick={handleAddToken}>
                    {addStatus === "deploying" ? (addMode === "deploy" ? "Deploying…" : "Registering…") : (addMode === "deploy" ? "Deploy & Register" : "Register Token")}
                  </button>
                </div>
              </div>
            )}

            {/* ── MANAGE TOKEN (on-chain config editor) ────────────────── */}
            {tab === "addtoken" && manageToken && (
              <div>
                <div className="ap-page-head">
                  <div>
                    <button className="ap-btn ap-btn-ghost ap-btn-sm" onClick={() => { setManageToken(null); setOnchainConfig(null); setManageMsg(""); }}>← Back</button>
                    <h1 className="ap-page-title" style={{ marginTop: 8, marginBottom: 0 }}>Manage {manageToken.api_token}</h1>
                  </div>
                  <button className="ap-btn ap-btn-sm" onClick={() => openManage(manageToken)}>Refresh</button>
                </div>

                {manageLoading && <Spinner />}
                {manageMsg && <Alert type={manageMsgType} onClose={() => setManageMsg("")}>{manageMsg}</Alert>}

                {onchainConfig && (
                  <>
                    {/* Live stats */}
                    <div className="ap-stat-grid" style={{ marginBottom: 20 }}>
                      <div className="ap-stat-card"><div className="ap-stat-label">Contract</div><div style={{ fontSize: 12, fontFamily: "monospace", color: "#f0eadb", marginTop: 4 }}>{shortAddr(onchainConfig.address)}</div></div>
                      <div className="ap-stat-card"><div className="ap-stat-label">Owner</div><div style={{ fontSize: 12, fontFamily: "monospace", color: "#f0eadb", marginTop: 4 }}>{shortAddr(onchainConfig.owner)}</div></div>
                      <div className="ap-stat-card"><div className="ap-stat-label">Tax Manager</div><div style={{ fontSize: 12, fontFamily: "monospace", color: "#f0eadb", marginTop: 4 }}>{shortAddr(onchainConfig.taxManager)}</div></div>
                      <div className="ap-stat-card"><div className="ap-stat-label">Transfer Tax</div><div className="ap-stat-num" style={{ fontSize: 22 }}>{(onchainConfig.transferTaxBps / 100).toFixed(2)}%</div></div>
                      <div className="ap-stat-card"><div className="ap-stat-label">Buy Tax</div><div className="ap-stat-num" style={{ fontSize: 22 }}>{((onchainConfig.buyTaxBps || 0) / 100).toFixed(2)}%</div></div>
                      <div className="ap-stat-card"><div className="ap-stat-label">Sell Tax</div><div className="ap-stat-num" style={{ fontSize: 22 }}>{((onchainConfig.sellTaxBps || 0) / 100).toFixed(2)}%</div></div>
                      <div className="ap-stat-card"><div className="ap-stat-label">Total Supply</div><div style={{ fontSize: 13, color: "#f0eadb", marginTop: 4, fontWeight: 700 }}>{parseFloat(onchainConfig.totalSupply) > 0 ? `${(BigInt(onchainConfig.totalSupply) / BigInt(10 ** manageToken.decimals)).toString()}` : "0"}</div></div>
                      <div className="ap-stat-card"><div className="ap-stat-label">Swapback</div><div style={{ fontSize: 13, color: onchainConfig.swapbackEnabled ? "#4ecb71" : "#888", marginTop: 4, fontWeight: 700 }}>{onchainConfig.swapbackEnabled ? `Active (${onchainConfig.swapbackToNative === false ? "ERC20" : "Native"})` : "Off"}</div></div>
                      <div className="ap-stat-card"><div className="ap-stat-label">LP Tax Share</div><div className="ap-stat-num" style={{ fontSize: 22 }}>{((onchainConfig.lpTaxBps || 0) / 100).toFixed(2)}%</div></div>
                    </div>

                    {/* ─ Tax config ─ */}
                    <div className="ap-manage-section">
                      <h2 className="ap-section-title">Tax Rates</h2>
                      <p className="ap-muted-text">Set 0 on all three to disable tax. Use different rates for AMM buys/sells; the Transfer rate applies to all other transfers.</p>
                      <div className="ap-form-stack">
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          <label className="ap-field-label" style={{ flex: 1, minWidth: 120 }}>Transfer Tax (%)
                            <input className="ap-field-input" type="number" min="0" max="50" step="0.01" placeholder="0" value={editTaxBps === "0" ? "" : String(parseInt(editTaxBps)/100)} onChange={(e) => setEditTaxBps(String(Math.round(parseFloat(e.target.value||"0")*100)))} />
                          </label>
                          <label className="ap-field-label" style={{ flex: 1, minWidth: 120 }}>Buy Tax (%)
                            <input className="ap-field-input" type="number" min="0" max="50" step="0.01" placeholder="0" value={editTaxBuyBps === "0" ? "" : String(parseInt(editTaxBuyBps)/100)} onChange={(e) => setEditTaxBuyBps(String(Math.round(parseFloat(e.target.value||"0")*100)))} />
                          </label>
                          <label className="ap-field-label" style={{ flex: 1, minWidth: 120 }}>Sell Tax (%)
                            <input className="ap-field-input" type="number" min="0" max="50" step="0.01" placeholder="0" value={editTaxSellBps === "0" ? "" : String(parseInt(editTaxSellBps)/100)} onChange={(e) => setEditTaxSellBps(String(Math.round(parseFloat(e.target.value||"0")*100)))} />
                          </label>
                        </div>
                        {Math.max(parseInt(editTaxBps)||0, parseInt(editTaxBuyBps)||0, parseInt(editTaxSellBps)||0) > 0 && (
                          <div>
                            <div className="ap-subsection-label">Tax Recipients — must total 100%</div>
                            {editTaxRows.map((row, i) => (
                              <div key={i} className="ap-tax-row">
                                <input className="ap-field-input" placeholder="0x… wallet" value={row.wallet} onChange={(e) => setEditTaxRows((r) => r.map((x, j) => j === i ? { ...x, wallet: e.target.value.trim() } : x))} style={{ flex: 3 }} />
                                <input className="ap-field-input" type="number" min="0" max="100" step="0.01" placeholder="%" value={row.pct} onChange={(e) => setEditTaxRows((r) => r.map((x, j) => j === i ? { ...x, pct: e.target.value } : x))} style={{ flex: 1, minWidth: 70 }} />
                                <button className="ap-btn ap-btn-sm ap-btn-danger" onClick={() => setEditTaxRows((r) => r.filter((_, j) => j !== i))} disabled={editTaxRows.length <= 1}>−</button>
                              </div>
                            ))}
                            <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center" }}>
                              <button className="ap-btn ap-btn-sm" onClick={() => setEditTaxRows((r) => [...r, { wallet: "", pct: "" }])}>+ Add Wallet</button>
                              <span className="ap-muted-text" style={{ fontSize: 11 }}>Total: {editTaxRows.reduce((s, r) => s + (parseFloat(r.pct)||0), 0).toFixed(2)}%</span>
                            </div>
                          </div>
                        )}
                        <button className="ap-btn ap-btn-primary" disabled={manageLoading} onClick={handleUpdateTax}>Update Tax On-Chain</button>
                      </div>
                    </div>

                    {/* ─ Swapback ─ */}
                    <div className="ap-manage-section">
                      <h2 className="ap-section-title">Auto-Swapback</h2>
                      <p className="ap-muted-text">When enabled the contract swaps accumulated tax tokens back to the output token (native BESC or an ERC20 rewards token) and distributes to recipients.</p>
                      <div className="ap-form-stack">
                        <label className="ap-check-label">
                          <input type="checkbox" checked={editSwapback.enabled} onChange={(e) => setEditSwapback((s) => ({ ...s, enabled: e.target.checked }))} />
                          Enabled
                        </label>
                        {editSwapback.enabled && (
                          <>
                            <div className="ap-tax-mode-row">
                              <span className="ap-field-label" style={{ marginBottom: 0 }}>Payout Type</span>
                              <button className={`ap-btn ap-btn-sm${editSwapback.toNative ? " ap-btn-primary" : " ap-btn-ghost"}`} onClick={() => setEditSwapback((s) => ({ ...s, toNative: true }))}>Native BESC</button>
                              <button className={`ap-btn ap-btn-sm${!editSwapback.toNative ? " ap-btn-primary" : " ap-btn-ghost"}`} onClick={() => setEditSwapback((s) => ({ ...s, toNative: false }))}>ERC20 Rewards Token</button>
                            </div>
                            <label className="ap-field-label">DEX Router<input className="ap-field-input" placeholder="0x…" value={editSwapback.router} onChange={(e) => setEditSwapback((s) => ({ ...s, router: e.target.value.trim() }))} /></label>
                            <label className="ap-field-label">{editSwapback.toNative ? "Output Token (WBESC/wrapped native)" : "Rewards Token Address (ERC20)"}<input className="ap-field-input" placeholder="0x…" value={editSwapback.tokenOut} onChange={(e) => setEditSwapback((s) => ({ ...s, tokenOut: e.target.value.trim() }))} /></label>
                            <label className="ap-field-label">Threshold (token amount before swap triggers)<input className="ap-field-input" placeholder="e.g. 1000" value={editSwapback.threshold} onChange={(e) => setEditSwapback((s) => ({ ...s, threshold: e.target.value }))} /></label>
                          </>
                        )}
                        <button className="ap-btn ap-btn-primary" disabled={manageLoading} onClick={handleUpdateSwapback}>Update Swapback On-Chain</button>
                      </div>
                    </div>

                    {/* ─ AMM Pairs ─ */}
                    <div className="ap-manage-section">
                      <h2 className="ap-section-title">AMM Pair Registration</h2>
                      <p className="ap-muted-text">Register the token's DEX liquidity pool address so the contract can detect buy/sell transactions and apply the correct tax rate automatically.</p>
                      <div className="ap-form-stack">
                        <label className="ap-field-label">LP Pair Contract Address<input className="ap-field-input" placeholder="0x… (token/WBESC pair)" value={ammPairInput} onChange={(e) => setAmmPairInput(e.target.value.trim())} /></label>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button className="ap-btn ap-btn-success" disabled={manageLoading} onClick={() => handleSetAMMPair(true)}>Register as AMM Pair</button>
                          <button className="ap-btn ap-btn-danger" disabled={manageLoading} onClick={() => handleSetAMMPair(false)}>Remove AMM Pair</button>
                        </div>
                      </div>
                    </div>

                    {/* ─ LP Config ─ */}
                    <div className="ap-manage-section">
                      <h2 className="ap-section-title">Auto-LP Config</h2>
                      <p className="ap-muted-text">Set the percentage of collected tax that gets re-added as liquidity (both sides). The contract swaps half to the paired token and calls addLiquidity automatically.</p>
                      <div className="ap-form-stack">
                        <label className="ap-field-label">LP Tax Share (% of collected tax sent to liquidity)
                          <input className="ap-field-input" type="number" min="0" max="100" step="0.01" placeholder="0" value={editLPConfig.lpBps === "0" ? "" : String((parseInt(editLPConfig.lpBps)||0)/100)} onChange={(e) => setEditLPConfig((s) => ({ ...s, lpBps: String(Math.round(parseFloat(e.target.value||"0")*100)) }))} />
                        </label>
                        <label className="ap-field-label">Paired Token Address (e.g. WBESC or USDT on BESC)
                          <input className="ap-field-input" placeholder="0x…" value={editLPConfig.pairedToken} onChange={(e) => setEditLPConfig((s) => ({ ...s, pairedToken: e.target.value.trim() }))} />
                        </label>
                        <button className="ap-btn ap-btn-primary" disabled={manageLoading} onClick={handleUpdateLPConfig}>Update LP Config On-Chain</button>
                      </div>
                    </div>

                    {/* ─ Tax Manager ─ */}
                    <div className="ap-manage-section">
                      <h2 className="ap-section-title">Tax Manager Address</h2>
                      <p className="ap-muted-text">The team's wallet that can update taxes and swapback settings directly on-chain.</p>
                      <div className="ap-form-stack">
                        <label className="ap-field-label">New Tax Manager Wallet<input className="ap-field-input" placeholder="0x…" value={editManager} onChange={(e) => setEditManager(e.target.value.trim())} /></label>
                        <button className="ap-btn ap-btn-primary" disabled={manageLoading} onClick={handleUpdateManager}>Update Manager On-Chain</button>
                      </div>
                    </div>

                    {/* ─ Tax Exemptions ─ */}
                    <div className="ap-manage-section">
                      <h2 className="ap-section-title">Tax Exemptions</h2>
                      <p className="ap-muted-text">Add or remove tax-exempt wallets (DEX routers, team multisigs, etc.).</p>
                      <div className="ap-form-stack">
                        <label className="ap-field-label">Wallet Address<input className="ap-field-input" placeholder="0x…" value={exemptWallet} onChange={(e) => setExemptWallet(e.target.value.trim())} /></label>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button className="ap-btn ap-btn-success" disabled={manageLoading} onClick={() => handleSetExempt(true)}>Set Exempt</button>
                          <button className="ap-btn ap-btn-danger" disabled={manageLoading} onClick={() => handleSetExempt(false)}>Remove Exemption</button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── SUPPORT TICKETS ──────────────────────────────────────── */}
            {tab === "support" && (
              <div>
                <div className="ap-page-head">
                  <h1 className="ap-page-title">Support Tickets</h1>
                  <button className="ap-btn ap-btn-sm" onClick={() => fetchSupportTickets()}>Refresh</button>
                </div>

                {supportMsg && <Alert type={supportMsgType} onClose={() => setSupportMsg("")}>{supportMsg}</Alert>}

                {/* Filter toggle */}
                <div className="ap-toggle-row" style={{ marginBottom: 16 }}>
                  {["open", "resolved"].map((f) => (
                    <button key={f} className={`ap-btn${supportFilter === f ? " ap-btn-primary" : " ap-btn-ghost"}`}
                      onClick={() => { setSupportFilter(f); setSupportSelected(null); fetchSupportTickets(f); }}>
                      {f.charAt(0).toUpperCase() + f.slice(1)} ({f === supportFilter ? supportTickets.length : "…"})
                    </button>
                  ))}
                </div>

                {supportLoading && <Spinner />}

                {/* Detail view */}
                {supportSelected && (() => {
                  const t = supportSelected;
                  const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
                  const API_KEY = process.env.REACT_APP_API_KEY || "";
                  const imgUrl = t.image_filename
                    ? `${BACKEND}/admin/support/${t.id}/image?x-api-key=${encodeURIComponent(API_KEY)}`
                    : null;
                  return (
                    <div className="ap-manage-section">
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                        <button className="ap-btn ap-btn-ghost ap-btn-sm" onClick={() => { setSupportSelected(null); setSupportNotesDraft(""); }}>← Back to list</button>
                        <div style={{ display: "flex", gap: 8 }}>
                          {t.status === "open"
                            ? <button className="ap-btn ap-btn-success" disabled={supportLoading} onClick={() => handleResolveTicket(t.id)}>Mark Resolved</button>
                            : <button className="ap-btn ap-btn-ghost" disabled={supportLoading} onClick={() => handleReopenTicket(t.id)}>Reopen</button>
                          }
                          <button className="ap-btn ap-btn-danger ap-btn-sm" disabled={supportLoading} onClick={() => handleDeleteTicket(t.id)}>Delete</button>
                        </div>
                      </div>

                      <div className="ap-stat-grid" style={{ marginBottom: 18 }}>
                        <div className="ap-stat-card"><div className="ap-stat-label">Ticket #</div><div style={{ fontSize: 20, fontWeight: 700, color: "#f0eadb" }}>#{t.id}</div></div>
                        <div className="ap-stat-card"><div className="ap-stat-label">Status</div><StatusPill status={t.status} /></div>
                        <div className="ap-stat-card"><div className="ap-stat-label">Submitted</div><div style={{ fontSize: 12, color: "#c8bfb0", marginTop: 4 }}>{fmt(t.created_at)}</div></div>
                        {t.resolved_at && <div className="ap-stat-card"><div className="ap-stat-label">Resolved</div><div style={{ fontSize: 12, color: "#c8bfb0", marginTop: 4 }}>{fmt(t.resolved_at)}</div></div>}
                      </div>

                      <div className="ap-form-stack">
                        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                          <div style={{ flex: 1, minWidth: 180 }}>
                            <div className="ap-subsection-label">Name</div>
                            <div style={{ color: "#f0eadb", marginTop: 4 }}>{t.name}</div>
                          </div>
                          <div style={{ flex: 1, minWidth: 180 }}>
                            <div className="ap-subsection-label">Email</div>
                            <a href={`mailto:${t.email}`} style={{ color: "#d4a843", marginTop: 4, display: "block" }}>{t.email}</a>
                          </div>
                        </div>

                        {(t.wallet_address || t.source_chain) && (
                          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                            {t.wallet_address && <div style={{ flex: 1 }}><div className="ap-subsection-label">Wallet</div><div style={{ fontFamily: "monospace", fontSize: 12, color: "#f0eadb", marginTop: 4, wordBreak: "break-all" }}>{t.wallet_address}</div></div>}
                            {t.source_chain && <div style={{ flex: 1 }}><div className="ap-subsection-label">Chain</div><div style={{ color: "#f0eadb", marginTop: 4 }}>{t.source_chain}</div></div>}
                          </div>
                        )}

                        {t.tx_hash && (
                          <div><div className="ap-subsection-label">Transaction Hash</div><div style={{ fontFamily: "monospace", fontSize: 12, color: "#f0eadb", marginTop: 4, wordBreak: "break-all" }}>{t.tx_hash}</div></div>
                        )}

                        <div>
                          <div className="ap-subsection-label">Issue Type</div>
                          <div style={{ color: "#f0eadb", marginTop: 4 }}>{t.issue_type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "Other"}</div>
                        </div>

                        <div>
                          <div className="ap-subsection-label">Message</div>
                          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,214,102,0.1)", borderRadius: 8, padding: "10px 14px", color: "#f0eadb", fontSize: 14, lineHeight: 1.6, marginTop: 4, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{t.message}</div>
                        </div>

                        {imgUrl && (
                          <div>
                            <div className="ap-subsection-label">Screenshot</div>
                            <img src={imgUrl} alt="Support screenshot" style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 8, border: "1px solid rgba(255,214,102,0.15)", marginTop: 6 }} />
                          </div>
                        )}

                        <div>
                          <div className="ap-subsection-label">Internal Notes</div>
                          <textarea className="ap-field-input" rows={4} style={{ marginTop: 6, resize: "vertical", fontFamily: "inherit" }}
                            placeholder="Add internal notes visible only to admins…"
                            value={supportNotesDraft}
                            onChange={(e) => setSupportNotesDraft(e.target.value)} />
                          <button className="ap-btn ap-btn-sm" style={{ marginTop: 6 }} onClick={() => handleSaveNotes(t.id)}>Save Notes</button>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Ticket list */}
                {!supportSelected && !supportLoading && (
                  supportTickets.length === 0
                    ? <div className="ap-empty-state">No {supportFilter} tickets.</div>
                    : <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {supportTickets.map((t) => (
                          <div key={t.id} className="ap-manage-section" style={{ cursor: "pointer", padding: "14px 16px" }}
                            onClick={() => { setSupportSelected(t); setSupportNotesDraft(t.admin_notes || ""); }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                              <div>
                                <span style={{ fontWeight: 700, color: "#f0eadb" }}>#{t.id} — {t.name}</span>
                                <span style={{ color: "#9a9080", fontSize: 12, marginLeft: 10 }}>{t.email}</span>
                                {t.image_filename && <span style={{ marginLeft: 8, fontSize: 11, color: "#d4a843" }}>📎 img</span>}
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <span style={{ fontSize: 11, color: "#665e52" }}>{fmt(t.created_at)}</span>
                                <StatusPill status={t.status} />
                              </div>
                            </div>
                            <div style={{ color: "#9a9080", fontSize: 13, marginTop: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              <span style={{ color: "#c8bfb0", marginRight: 6 }}>{t.issue_type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}:</span>
                              {t.message}
                            </div>
                          </div>
                        ))}
                      </div>
                )}
              </div>
            )}

            {/* ── SECURITY ─────────────────────────────────────────────── */}
            {tab === "security" && (
              <div>
                <div className="ap-page-head">
                  <h1 className="ap-page-title">Security Monitor</h1>
                  <button className="ap-btn ap-btn-sm" onClick={() => { fetchSecEvents(); fetchBlockedWallets(); }}>Refresh</button>
                </div>

                {secMsg && <Alert type={secMsgType} onClose={() => setSecMsg("")}>{secMsg}</Alert>}

                <div className="ap-toggle-row" style={{ marginBottom: 16 }}>
                  <button className={`ap-btn${secView === "events" ? " ap-btn-primary" : " ap-btn-ghost"}`} onClick={() => setSecView("events")}>Exploit Attempts</button>
                  <button className={`ap-btn${secView === "blocked" ? " ap-btn-primary" : " ap-btn-ghost"}`} onClick={() => setSecView("blocked")}>Blocked ({blockedWallets.length})</button>
                </div>

                {secView === "events" && (
                  <>
                    {/* Desktop */}
                    <div className="ap-table-wrap ap-desktop-only">
                      <table className="ap-table">
                        <thead><tr><th>Time</th><th>Event</th><th>Wallet</th><th>IP</th><th>Detail</th><th>Block</th></tr></thead>
                        <tbody>
                          {secEvents.map((e) => (
                            <tr key={e.id}>
                              <td>{fmt(e.created_at)}</td>
                              <td><span className="ap-event-tag">{e.event_type}</span></td>
                              <td title={e.wallet_address}>{shortAddr(e.wallet_address)}</td>
                              <td>{e.ip || "—"}</td>
                              <td style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{e.detail || "—"}</td>
                              <td>{e.wallet_address && <button className="ap-btn ap-btn-sm ap-btn-danger" onClick={() => handleBlock(e.wallet_address, `Blocked after ${e.event_type}`)}>Block</button>}</td>
                            </tr>
                          ))}
                          {secEvents.length === 0 && !loading && <tr><td colSpan={6} className="ap-empty">No security events logged</td></tr>}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile */}
                    <div className="ap-mobile-only ap-tx-cards">
                      {secEvents.map((e) => (
                        <div key={e.id} className="ap-tx-card ap-tx-card--warn">
                          <div className="ap-tx-card-head">
                            <span className="ap-event-tag">{e.event_type}</span>
                            <span className="ap-muted-text">{fmt(e.created_at)}</span>
                          </div>
                          <div className="ap-tx-card-row"><span className="ap-tx-card-key">Wallet</span><span className="ap-mono">{shortAddr(e.wallet_address)}</span></div>
                          <div className="ap-tx-card-row"><span className="ap-tx-card-key">IP</span><span>{e.ip || "—"}</span></div>
                          {e.detail && <div className="ap-tx-card-row"><span className="ap-tx-card-key">Detail</span><span style={{ fontSize: 11 }}>{e.detail}</span></div>}
                          {e.wallet_address && (
                            <div className="ap-tx-card-action">
                              <button className="ap-btn ap-btn-sm ap-btn-danger ap-btn-wide" onClick={() => handleBlock(e.wallet_address, `Blocked after ${e.event_type}`)}>Block This Wallet</button>
                            </div>
                          )}
                        </div>
                      ))}
                      {secEvents.length === 0 && !loading && <div className="ap-empty">No security events logged</div>}
                    </div>
                  </>
                )}

                {secView === "blocked" && (
                  <>
                    <div className="ap-form-stack" style={{ marginBottom: 16 }}>
                      <label className="ap-field-label">Wallet Address to Block<input className="ap-field-input" placeholder="0x…" value={blockInput} onChange={(e) => setBlockInput(e.target.value.trim())} /></label>
                      <label className="ap-field-label">Reason (optional)<input className="ap-field-input" placeholder="e.g. Replay attack attempt" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} /></label>
                      <button className="ap-btn ap-btn-danger" onClick={() => handleBlock()}>Block Wallet</button>
                    </div>

                    {/* Desktop */}
                    <div className="ap-table-wrap ap-desktop-only">
                      <table className="ap-table">
                        <thead><tr><th>Address</th><th>Reason</th><th>Blocked At</th><th></th></tr></thead>
                        <tbody>
                          {blockedWallets.map((b) => (
                            <tr key={b.id}>
                              <td title={b.address}>{shortAddr(b.address)}</td>
                              <td>{b.reason || "—"}</td>
                              <td>{fmt(b.blocked_at)}</td>
                              <td><button className="ap-btn ap-btn-sm ap-btn-success" onClick={() => handleUnblock(b.address)}>Unblock</button></td>
                            </tr>
                          ))}
                          {blockedWallets.length === 0 && !loading && <tr><td colSpan={4} className="ap-empty">No blocked wallets</td></tr>}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile */}
                    <div className="ap-mobile-only ap-tx-cards">
                      {blockedWallets.map((b) => (
                        <div key={b.id} className="ap-tx-card">
                          <div className="ap-tx-card-head">
                            <span className="ap-mono">{shortAddr(b.address)}</span>
                            <span className="ap-muted-text">{fmt(b.blocked_at)}</span>
                          </div>
                          {b.reason && <div className="ap-tx-card-row"><span className="ap-tx-card-key">Reason</span><span>{b.reason}</span></div>}
                          <div className="ap-tx-card-action">
                            <button className="ap-btn ap-btn-sm ap-btn-success" onClick={() => handleUnblock(b.address)}>Unblock</button>
                          </div>
                        </div>
                      ))}
                      {blockedWallets.length === 0 && !loading && <div className="ap-empty">No blocked wallets</div>}
                    </div>
                  </>
                )}
              </div>
            )}

          </main>{/* end ap-main-scroll */}
        </div>{/* end ap-content-wrap */}
      </div>{/* end ap-overlay */}
    </>
  );
}

/* ─── Styles ─────────────────────────────────────────────────────────────────── */
function APStyles() {
  return (
    <style>{`
      /* ── Tokens ── */
      :root {
        --ap-gold: #d4a843;
        --ap-gold-bg: rgba(212,168,67,0.14);
        --ap-gold-border: rgba(212,168,67,0.22);
        --ap-green: #4ecb71;
        --ap-red: #e05c5c;
        --ap-bg: #080c18;
        --ap-surface: #0d1226;
        --ap-surface2: rgba(255,255,255,0.04);
        --ap-text: #f0eadb;
        --ap-muted: rgba(240,234,219,0.45);
        --ap-border: rgba(255,255,255,0.08);
        --ap-bottom-h: 64px;
      }

      /* ── Full-screen overlay — covers Header and Footer ── */
      .ap-overlay {
        position: fixed;
        inset: 0;
        z-index: 9999;
        background: var(--ap-bg);
        color: var(--ap-text);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        font-size: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      /* ── Login ── */
      .ap-login-box {
        width: 100%;
        max-width: 400px;
        background: var(--ap-surface);
        border: 1px solid var(--ap-gold-border);
        border-radius: 16px;
        padding: 40px 32px;
        box-shadow: 0 32px 80px rgba(0,0,0,0.7);
        margin: 0 16px;
      }
      .ap-login-logo { text-align: center; margin-bottom: 32px; }
      .ap-login-chain-name { font-size: 30px; font-weight: 900; color: var(--ap-gold); letter-spacing: 3px; }
      .ap-login-sub-text { font-size: 11px; color: var(--ap-muted); letter-spacing: 4px; text-transform: uppercase; margin-top: 6px; }
      .ap-login-form { display: flex; flex-direction: column; gap: 16px; }
      .ap-login-note { text-align: center; color: var(--ap-muted); font-size: 11px; margin-top: 24px; margin-bottom: 0; }

      /* ── Panel layout ── */
      .ap-panel { align-items: stretch; justify-content: flex-start; overflow: hidden; }

      /* ── Sidebar (desktop) ── */
      .ap-sidebar {
        width: 210px;
        flex-shrink: 0;
        background: var(--ap-surface);
        border-right: 1px solid var(--ap-border);
        display: flex;
        flex-direction: column;
        padding: 24px 0;
        height: 100%;
        overflow-y: auto;
      }
      .ap-sidebar-brand { padding: 0 20px 20px; border-bottom: 1px solid var(--ap-border); margin-bottom: 12px; }
      .ap-brand-name { display: block; font-size: 20px; font-weight: 900; color: var(--ap-gold); letter-spacing: 2px; }
      .ap-brand-sub { display: block; font-size: 10px; color: var(--ap-muted); letter-spacing: 3px; text-transform: uppercase; margin-top: 2px; }
      .ap-sidebar-nav { flex: 1; display: flex; flex-direction: column; gap: 2px; padding: 0 10px; }
      .ap-side-tab {
        display: flex; align-items: center; gap: 10px;
        background: transparent; border: none; border-radius: 8px;
        color: var(--ap-muted); cursor: pointer;
        padding: 10px 12px; font-size: 13px; font-weight: 500;
        text-align: left; width: 100%; transition: all 0.15s;
      }
      .ap-side-tab:hover { background: var(--ap-surface2); color: var(--ap-text); }
      .ap-side-tab--active { background: var(--ap-gold-bg); color: var(--ap-gold); font-weight: 700; }
      .ap-side-icon { width: 18px; text-align: center; font-size: 14px; }
      .ap-sidebar-footer { padding: 16px 20px; border-top: 1px solid var(--ap-border); }

      /* ── Content wrap ── */
      .ap-content-wrap {
        flex: 1;
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
        min-width: 0;
      }

      /* ── Top bar (mobile) ── */
      .ap-topbar {
        display: none;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        background: var(--ap-surface);
        border-bottom: 1px solid var(--ap-border);
        flex-shrink: 0;
      }
      .ap-topbar-title { font-size: 16px; font-weight: 800; color: var(--ap-gold); letter-spacing: 1px; }

      /* ── Bottom tab bar (mobile) ── */
      .ap-bottombar {
        display: none;
        position: fixed;
        bottom: 0; left: 0; right: 0;
        height: var(--ap-bottom-h);
        background: var(--ap-surface);
        border-top: 1px solid var(--ap-border);
        z-index: 10001;
        flex-shrink: 0;
      }
      .ap-bottom-tab {
        flex: 1; display: flex; flex-direction: column; align-items: center;
        justify-content: center; gap: 3px;
        background: transparent; border: none;
        color: var(--ap-muted); cursor: pointer; padding: 8px 4px;
        font-size: 10px; font-weight: 600; transition: color 0.15s;
        text-transform: uppercase; letter-spacing: 0.04em;
      }
      .ap-bottom-tab--active { color: var(--ap-gold); }
      .ap-bottom-icon { font-size: 18px; line-height: 1; }
      .ap-bottom-label { font-size: 9px; }

      /* ── Main scroll area ── */
      .ap-main-scroll {
        flex: 1;
        overflow-y: auto;
        padding: 28px 32px;
        -webkit-overflow-scrolling: touch;
      }

      /* ── Page elements ── */
      .ap-page-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; gap: 12px; }
      .ap-page-title { font-size: 22px; font-weight: 800; color: var(--ap-text); margin: 0 0 20px; }
      .ap-page-head .ap-page-title { margin-bottom: 0; }
      .ap-section-title { font-size: 11px; font-weight: 700; color: var(--ap-muted); text-transform: uppercase; letter-spacing: 0.1em; margin: 24px 0 10px; }
      .ap-muted-text { color: var(--ap-muted); font-size: 13px; }

      /* ── Alerts ── */
      .ap-alert { border-radius: 8px; padding: 11px 14px; font-size: 13px; margin-bottom: 14px; display: flex; align-items: center; gap: 8px; }
      .ap-alert-error { background: rgba(224,92,92,0.12); border: 1px solid var(--ap-red); color: var(--ap-red); }
      .ap-alert-success { background: rgba(78,203,113,0.12); border: 1px solid var(--ap-green); color: var(--ap-green); }
      .ap-alert-warn { background: rgba(212,168,67,0.12); border: 1px solid var(--ap-gold); color: var(--ap-gold); }
      .ap-alert-info { background: var(--ap-surface2); border: 1px solid var(--ap-border); color: var(--ap-text); }
      .ap-close-btn { background: transparent; border: none; color: inherit; cursor: pointer; font-size: 14px; opacity: 0.7; margin-left: auto; padding: 0 4px; }

      /* ── Buttons ── */
      .ap-btn {
        background: var(--ap-surface2);
        border: 1px solid var(--ap-gold-border);
        color: var(--ap-gold);
        border-radius: 8px; padding: 8px 16px;
        cursor: pointer; font-size: 13px; font-weight: 600;
        transition: all 0.15s; white-space: nowrap; display: inline-flex; align-items: center; gap: 6px;
      }
      .ap-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      .ap-btn:hover:not(:disabled) { background: var(--ap-gold-bg); }
      .ap-btn-primary { background: var(--ap-gold-bg); border-color: var(--ap-gold); }
      .ap-btn-ghost { background: transparent; border-color: var(--ap-border); color: var(--ap-muted); }
      .ap-btn-ghost:hover:not(:disabled) { color: var(--ap-text); background: var(--ap-surface2); }
      .ap-btn-sm { padding: 5px 12px; font-size: 12px; }
      .ap-btn-block { width: 100%; justify-content: center; padding: 13px; font-size: 15px; }
      .ap-btn-wide { width: 100%; justify-content: center; }
      .ap-btn-danger { background: rgba(224,92,92,0.1); border-color: var(--ap-red); color: var(--ap-red); }
      .ap-btn-danger:hover:not(:disabled) { background: rgba(224,92,92,0.2); }
      .ap-btn-success { background: rgba(78,203,113,0.1); border-color: var(--ap-green); color: var(--ap-green); }
      .ap-btn-success:hover:not(:disabled) { background: rgba(78,203,113,0.2); }
      .ap-inline-link { background: none; border: none; color: var(--ap-gold); cursor: pointer; padding: 0; font-weight: 600; font-size: 13px; }

      /* ── Fields ── */
      .ap-field-label { display: flex; flex-direction: column; gap: 6px; font-size: 12px; color: var(--ap-muted); font-weight: 600; }
      .ap-field-input {
        background: rgba(255,255,255,0.05);
        border: 1px solid var(--ap-border);
        color: var(--ap-text); border-radius: 8px;
        padding: 10px 12px; font-size: 14px;
        outline: none; width: 100%;
        -webkit-appearance: none;
        box-sizing: border-box;
        transition: border-color 0.15s;
      }
      .ap-field-input:focus { border-color: var(--ap-gold); }
      .ap-field-input::placeholder { color: var(--ap-muted); }
      .ap-select {
        background: rgba(255,255,255,0.05);
        border: 1px solid var(--ap-border);
        color: var(--ap-text); border-radius: 8px;
        padding: 10px; font-size: 13px; cursor: pointer; outline: none;
      }
      .ap-select-full { width: 100%; }
      .ap-search-input { flex: 1; min-width: 0; }

      /* ── Filter bar ── */
      .ap-filter-bar { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
      .ap-filter-bar--search { margin-bottom: 14px; }

      /* ── Tables (desktop only) ── */
      .ap-table-wrap { overflow-x: auto; border-radius: 10px; border: 1px solid var(--ap-border); }
      .ap-table { width: 100%; border-collapse: collapse; font-size: 12px; }
      .ap-table thead { background: rgba(255,255,255,0.03); }
      .ap-table th { text-align: left; padding: 9px 12px; color: var(--ap-muted); font-weight: 700; font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em; white-space: nowrap; border-bottom: 1px solid var(--ap-border); }
      .ap-table td { padding: 9px 12px; border-bottom: 1px solid rgba(255,255,255,0.04); color: var(--ap-text); vertical-align: middle; white-space: nowrap; }
      .ap-table tr:last-child td { border-bottom: none; }
      .ap-table tr:hover td { background: var(--ap-surface2); }
      .ap-empty { text-align: center; color: var(--ap-muted); padding: 28px 16px; }

      /* ── Transaction cards (mobile) ── */
      .ap-tx-cards { display: flex; flex-direction: column; gap: 10px; }
      .ap-tx-card {
        background: var(--ap-surface);
        border: 1px solid var(--ap-border);
        border-radius: 10px; padding: 14px 16px;
        display: flex; flex-direction: column; gap: 6px;
      }
      .ap-tx-card--warn { border-color: rgba(224,92,92,0.3); background: rgba(224,92,92,0.04); }
      .ap-tx-card-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 4px; }
      .ap-tx-card-route { font-size: 13px; font-weight: 700; color: var(--ap-text); flex: 1; }
      .ap-tx-card-time { font-size: 11px; color: var(--ap-muted); }
      .ap-tx-card-row { display: flex; align-items: baseline; gap: 8px; font-size: 12px; }
      .ap-tx-card-key { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--ap-muted); min-width: 70px; flex-shrink: 0; }
      .ap-tx-card-error { background: rgba(224,92,92,0.1); border: 1px solid rgba(224,92,92,0.25); border-radius: 6px; padding: 6px 10px; font-size: 11px; color: var(--ap-red); margin-top: 4px; }
      .ap-tx-card-action { margin-top: 8px; }
      .ap-mono { font-family: 'SF Mono', 'Fira Code', monospace; font-size: 12px; word-break: break-all; }

      /* ── Token cards ── */
      .ap-token-list { display: flex; flex-direction: column; gap: 10px; margin-bottom: 24px; }
      .ap-token-card { background: var(--ap-surface); border: 1px solid var(--ap-border); border-radius: 10px; padding: 14px 16px; display: flex; flex-direction: column; gap: 6px; }
      .ap-token-card-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 4px; }
      .ap-token-card-action { margin-top: 8px; }

      /* ── Status pill ── */
      .ap-pill { display: inline-block; padding: 2px 8px; border-radius: 20px; border: 1px solid; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; flex-shrink: 0; }

      /* ── Dashboard ── */
      .ap-stat-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 10px; margin-bottom: 4px; }
      .ap-stat-card { background: var(--ap-surface); border: 1px solid var(--ap-border); border-radius: 10px; padding: 16px; }
      .ap-stat-card--highlight { border-color: var(--ap-gold-border); }
      .ap-stat-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--ap-muted); margin-bottom: 8px; }
      .ap-stat-num { font-size: 32px; font-weight: 800; }
      .ap-route-list { display: flex; flex-direction: column; gap: 6px; margin-bottom: 4px; }
      .ap-route-row { display: flex; align-items: center; gap: 10px; background: var(--ap-surface); border: 1px solid var(--ap-border); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
      .ap-route-chain { font-weight: 700; min-width: 44px; }
      .ap-route-arrow { color: var(--ap-muted); }
      .ap-route-count { margin-left: auto; color: var(--ap-muted); font-size: 12px; }
      .ap-chip-row { display: flex; flex-wrap: wrap; gap: 8px; }
      .ap-chip { background: var(--ap-surface); border: 1px solid var(--ap-border); border-radius: 20px; padding: 5px 14px; font-size: 12px; display: flex; gap: 8px; align-items: center; }
      .ap-chip span { color: var(--ap-muted); }

      /* ── Forms ── */
      .ap-form-stack { display: flex; flex-direction: column; gap: 14px; margin-bottom: 16px; }
      .ap-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
      .ap-toggle-row { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
      .ap-info-box { background: rgba(212,168,67,0.06); border: 1px solid rgba(212,168,67,0.18); border-radius: 10px; padding: 14px 16px; font-size: 13px; line-height: 1.7; }

      /* ── Tax/Swapback subsections ── */
      .ap-subsection { background: rgba(255,255,255,0.025); border: 1px solid var(--ap-border); border-radius: 10px; padding: 14px 16px; display: flex; flex-direction: column; gap: 10px; }
      .ap-subsection-head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; font-size: 13px; font-weight: 700; color: var(--ap-text); }
      .ap-subsection-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--ap-muted); margin-bottom: 4px; }
      .ap-tax-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
      .ap-tax-mode-row { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 6px; }
      .ap-check-label { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--ap-text); cursor: pointer; font-weight: 600; }
      .ap-check-label input[type="checkbox"] { width: 16px; height: 16px; accent-color: var(--ap-gold); cursor: pointer; flex-shrink: 0; }
      .ap-manage-section { background: var(--ap-surface); border: 1px solid var(--ap-border); border-radius: 12px; padding: 18px 18px 16px; margin-bottom: 14px; }
      .ap-manage-section .ap-section-title { margin-top: 0; }

      /* ── Pagination ── */
      .ap-pagination { display: flex; gap: 8px; align-items: center; justify-content: flex-end; margin-top: 14px; flex-wrap: wrap; }

      /* ── Update row ── */
      .ap-update-row { background: var(--ap-surface); border: 1px solid var(--ap-gold-border); border-radius: 10px; padding: 14px 16px; margin-bottom: 14px; }
      .ap-update-label { font-weight: 600; font-size: 13px; margin-bottom: 10px; }
      .ap-update-controls { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }

      /* ── Security ── */
      .ap-event-tag { background: rgba(224,92,92,0.12); color: var(--ap-red); border-radius: 4px; padding: 2px 6px; font-size: 10px; font-weight: 700; text-transform: uppercase; }

      /* ── Spinner ── */
      .ap-spinner-wrap { display: flex; justify-content: center; padding: 24px; }
      .ap-spinner { width: 26px; height: 26px; border: 2px solid var(--ap-border); border-top-color: var(--ap-gold); border-radius: 50%; animation: ap-spin 0.7s linear infinite; }
      @keyframes ap-spin { to { transform: rotate(360deg); } }

      /* ── Responsive: hide/show ── */
      .ap-desktop-only { display: block; }
      .ap-mobile-only { display: none; }

      /* ── Mobile breakpoint ─────────────────────────────── */
      @media (max-width: 700px) {
        .ap-sidebar { display: none; }
        .ap-topbar { display: flex; }
        .ap-bottombar { display: flex; }

        .ap-desktop-only { display: none !important; }
        .ap-mobile-only { display: block; }
        .ap-tx-cards { display: flex; }

        .ap-main-scroll {
          padding: 16px 14px;
          padding-bottom: calc(var(--ap-bottom-h) + 16px);
        }

        .ap-form-row { grid-template-columns: 1fr; }

        .ap-filter-bar { gap: 6px; }
        .ap-filter-bar .ap-select { flex: 1; min-width: 0; font-size: 13px; }

        .ap-page-title { font-size: 18px; }
        .ap-stat-grid { grid-template-columns: 1fr 1fr; }

        .ap-update-controls { flex-direction: column; align-items: stretch; }
      }
    `}</style>
  );
}
